import { useState, useEffect } from 'react';
// eslint-disable-next-line no-unused-vars
import { isMobile, isIOS, isAndroid } from '../utils/pwaUtils';
import './PWAInstallPage.css';

export default function PWAInstallFromProfilePage({ onInstall, onSkip }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [platform, setPlatform] = useState('unknown');
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    // Определяем платформу пользователя
    const platformType = isIOS() ? 'ios' : isAndroid() ? 'android' : 'unknown';
    setPlatform(platformType);

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    const handleAppInstalled = () => {
      setIsInstallable(false);
      setDeferredPrompt(null);
      setIsInstalling(false);
      // После установки возвращаемся на главную страницу
      onSkip();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [onSkip]);

  const handleInstallClick = async () => {
    if (platform === 'ios') {
      // Для iOS показываем инструкции
      setShowIOSInstructions(true);
    } else if (deferredPrompt) {
      // Для Android и Windows/других платформ сразу запускаем установку
      setIsInstalling(true);
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;

        if (outcome === 'accepted') {
          setIsInstallable(false);
          setDeferredPrompt(null);
          // После успешной установки возвращаемся на главную страницу
          onSkip();
        }
      } catch (error) {
        console.error('Installation error:', error);
      } finally {
        setIsInstalling(false);
      }
    }
  };

  // Если показываем инструкции для iOS
  if (showIOSInstructions) {
    return (
      <div className="pwa-install-page">
        <div className="pwa-install-content">
          <h1 className="pwa-install-title">Инструкция для iOS</h1>

          <p className="pwa-install-description">
            Нажмите <span className="share-icon">
              <svg viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.75 5L9 2.75M9 2.75L11.25 5M9 2.75V10.25M6.37517 6.875C5.67626 6.875 5.3268 6.875 5.05115 6.98918C4.68361 7.14142 4.39142 7.43361 4.23918 7.80115C4.125 8.0768 4.125 8.42609 4.125 9.125V12.725C4.125 13.5651 4.125 13.9848 4.28849 14.3057C4.4323 14.5879 4.6616 14.8179 4.94385 14.9617C5.2644 15.125 5.68425 15.125 6.52268 15.125H11.4777C12.3161 15.125 12.7354 15.125 13.0559 14.9617C13.3382 14.8179 13.5679 14.5879 13.7117 14.3057C13.875 13.9851 13.875 13.5658 13.875 12.7273V9.125C13.875 8.42609 13.8749 8.0768 13.7607 7.80115C13.6085 7.43361 13.3166 7.14142 12.949 6.98918C12.6734 6.875 12.3239 6.875 11.625 6.875" stroke="#007BFF" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </span> в&nbsp;меню браузера и&nbsp;выберите
            «На&nbsp;экран Домой». Иконка Sense появится на&nbsp;экране вашего телефона
          </p>

          <div className="pwa-install-icon">
            <img
              src="/app-install-2-ios.png"
              alt="iOS Installation Instructions"
              className="pwa-install-image"
            />
          </div>

          <button className="pwa-install-skip" onClick={onSkip}>
            Пропустить установку
          </button>
        </div>
      </div>
    );
  }

  // Основной экран установки
  return (
    <div className="pwa-install-page">
      <div className="pwa-install-content">
        <div className="pwa-install-icon">
          <img
            src="/app-install.svg"
            alt="PWA Installation"
            className="pwa-install-image"
          />
        </div>

        <h1 className="pwa-install-title">Установите приложение</h1>

        <p className="pwa-install-description">
          Добавьте иконку Sense на&nbsp;главный экран для&nbsp;удобного использования
        </p>

        <button
          className={`pwa-install-button ${isInstalling ? 'installing' : ''}`}
          onClick={handleInstallClick}
          disabled={(!isInstallable && !['ios'].includes(platform) && !deferredPrompt) || isInstalling}
        >
          <span className="install-icon">
            <svg viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 12.5H12M12 12.5H16M12 12.5V16.5M12 12.5V8.5M4 17.3002V7.7002C4 6.58009 4 6.01962 4.21799 5.5918C4.40973 5.21547 4.71547 4.90973 5.0918 4.71799C5.51962 4.5 6.08009 4.5 7.2002 4.5H16.8002C17.9203 4.5 18.4801 4.5 18.9079 4.71799C19.2842 4.90973 19.5905 5.21547 19.7822 5.5918C20.0002 6.01962 20.0002 6.57967 20.0002 7.69978V17.2998C20.0002 18.4199 20.0002 18.98 19.7822 19.4078C19.5905 19.7841 19.2842 20.0905 18.9079 20.2822C18.4805 20.5 17.9215 20.5 16.8036 20.5H7.19691C6.07899 20.5 5.5192 20.5 5.0918 20.2822C5.51947 20.0905 4.71547 19.7842 4.21799 19.4079C4 18.9801 4 18.4203 4 17.3002Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </span>
          {isInstalling ? 'Установка...' : 'Установить'}
        </button>

        <button className="pwa-install-skip" onClick={onSkip}>
          Пропустить установку
        </button>
      </div>
    </div>
  );
}
